/**
 * Copy & Paste of image edits - the single source for every surface that
 * offers it: the Edit Image tool, the PDP silo/lifestyle cards and their
 * viewer footer, and the render/preview list cards.
 *
 * A copy is held in `copyInfo` (Redux, persisted to localStorage, synced
 * across tabs, expires after an hour) and, best effort, on the OS clipboard so
 * it survives a copy made in another tab or app.
 */
import React from 'react';
import { EDIT_IMAGE_TOOLS, IMAGINE_EDITOR_COPY_KEY } from '../constants/constants';
import { ACTION } from '../common/ThumbnailCardUI';
import { CopyIcon } from '../assets/icons';
import { EditIcon } from '../components/icons/EditIcon';
import { setCopyInfo } from '../redux/slicers/appSlicer';
import { successToast, warningToast } from './toastHelper';
import { writeTextToClipboardSafely } from './jsHelper';

/**
 * Where an image keeps its saved edits depends on the list it came from: PDP
 * rows nest them under render_image_details, render/preview cards carry them
 * at the top level.
 */
const getImageEditConfig = (image) =>
  image?.render_image_details?.edit_config ?? image?.edit_config;

/**
 * An edit_config only counts as real edits when it carries at least one slider
 * value. Applying a Reset stores `{}`, and `{}` is truthy - without this it
 * travels as a "copy" and pastes a silent no-op over the target image.
 */
export const hasEditConfigValues = (config) =>
  !!config &&
  typeof config === 'object' &&
  Object.values(EDIT_IMAGE_TOOLS).some((option) => config[option] !== undefined);

/** Copy is offered only on an image the editor has actually touched. */
export const canCopyImageEdits = (image) => {
  const config = getImageEditConfig(image);
  return hasEditConfigValues(config) && config.isImageTouched === true;
};

/** Paste is offered on every image except the one the copy came from. */
export const canPasteImageEdits = (copyInfo, imageId) =>
  !!copyInfo?.hasCopiedEdits && copyInfo.id !== imageId;

/**
 * Read back what was copied. copyInfo comes first - it is readable in a tab
 * where the OS clipboard isn't - with the clipboard as the fallback for a copy
 * made in another tab or app.
 */
export const resolveCopiedEditConfig = async (copyInfo) => {
  if (hasEditConfigValues(copyInfo?.config)) return copyInfo.config;

  try {
    const text = await navigator.clipboard.readText();
    if (!text?.trim()) return null;
    const copied = JSON.parse(text)?.[IMAGINE_EDITOR_COPY_KEY] || null;
    return hasEditConfigValues(copied) ? copied : null;
  } catch (error) {
    console.warn('Failed to read copied edits from clipboard:', error);
    return null;
  }
};

/**
 * The one copy implementation. `id` is whatever the surface's paste buttons
 * compare against, so the image a copy came from doesn't offer to paste over
 * itself.
 */
export const copyEditConfig = async ({ config, id, dispatch }) => {
  if (!hasEditConfigValues(config)) {
    warningToast('No edits to copy. Please edit the image first.');
    return;
  }

  dispatch(setCopyInfo({ hasCopiedEdits: true, id, config }));
  try {
    await writeTextToClipboardSafely(JSON.stringify({ [IMAGINE_EDITOR_COPY_KEY]: config }));
  } catch (error) {
    // Clipboard write is best effort; copyInfo already holds the config.
    console.warn('Failed to copy settings to clipboard:', error);
  }
  successToast('Settings copied successfully.');
};

/** Copy the saved edits off a card or list row. */
export const copyImageEdits = ({ image, dispatch }) =>
  copyEditConfig({ config: getImageEditConfig(image), id: image?.id, dispatch });

/**
 * The one paste-and-apply implementation: writes the copied config against the
 * given render images, then hands the applied config to `onApplied` so the
 * caller can refresh - and, on the product page, bake the new look into the
 * stored file. The editor pastes into its own sliders and saves on Apply
 * instead, so it doesn't come through here.
 */
export const applyCopiedEdits = async ({
  copyInfo,
  renderImageIds,
  updateBulkRenderImageConfig,
  onApplied,
}) => {
  try {
    const copiedConfig = await resolveCopiedEditConfig(copyInfo);
    if (!copiedConfig) {
      warningToast("This doesn't appear to be copied edits. Please copy valid image edits.");
      return;
    }

    const ids = [].concat(renderImageIds ?? []).filter(Boolean);
    if (!ids.length) {
      warningToast('Image is missing required data. Cannot apply edits.');
      return;
    }

    const updateImage = await updateBulkRenderImageConfig({
      payload: {
        render_image_ids: ids.join(','),
        // isImageTouched is what the Copy gate reads, and what Apply stores, so
        // a pasted image has to carry it too - without it the row comes back
        // from a refetch no longer offering to copy its own edits.
        edit_config: JSON.stringify({ ...copiedConfig, edited: false, isImageTouched: true }),
      },
    });

    // On failure the API layer already surfaces the backend error message, so
    // only toast on success to avoid a duplicate toast.
    if (updateImage?.status) {
      // Awaited, and the toast held behind it: a caller that also bakes the
      // pasted look into the stored image shouldn't be told it is done while
      // that upload is still in flight.
      await onApplied?.(copiedConfig);
      successToast('Settings pasted and applied successfully!');
    }
  } catch (error) {
    console.error('Failed to paste and apply settings:', error);
    warningToast('An unexpected error occurred while applying edits. Please try again.');
  }
};

/**
 * Copy / Paste entries for the 3-dot menu on render & preview list cards. The
 * card shows one camera at a time but a paste there applies to every camera of
 * the item, which is why it takes the whole `itemImages` list.
 */
export const getCopyPasteActions = ({
  copyInfo,
  activeCameraId,
  currentImage,
  dispatcher,
  designV2,
  itemImages,
  updateBulkRenderImageConfig,
  refetchList,
  trackClickEvent,
  rest,
}) => {
  const actions = {};
  const track = (name) =>
    trackClickEvent(name, { file_name: rest?.title, module_name: 'Image Editor' });

  if (canCopyImageEdits(currentImage)) {
    actions[ACTION.COPY] = {
      label: 'Copy Edits',
      icon: designV2 ? CopyIcon : <CopyIcon />,
      onClick: async () => {
        await copyImageEdits({ image: currentImage, dispatch: dispatcher });
        track('Copy');
      },
    };
  }

  if (canPasteImageEdits(copyInfo, activeCameraId)) {
    actions[ACTION.PASTE] = {
      label: 'Paste & Apply',
      icon: designV2 ? EditIcon : <EditIcon />,
      onClick: async () => {
        const ids = (itemImages || []).map((image) => image?.id);
        if (!ids.length || ids.some((id) => !id)) {
          warningToast('Some images are missing required data. Cannot apply edits.');
          return;
        }
        await applyCopiedEdits({
          copyInfo,
          renderImageIds: ids,
          updateBulkRenderImageConfig,
          onApplied: refetchList,
        });
        track('Paste');
      },
    };
  }

  return actions;
};
